<template>
  <v-dialog
    :fullscreen="isMobile"
    v-model="internalModelValue"
    width="550px"
  >
    <v-card>
      <v-card-title class="material-preview__header">
        <span>{{ $t('ad-materials.preview.title') }}</span>
        <v-btn
          icon="mdi-close"
          @click="close"
        />
      </v-card-title>
      <v-card-text>
        <r-img
          v-if="isImage"
          :src="material?.url"
        />
        <div
          v-else-if="isVideo"
          class="video-preview-container"
        >
          <video
            :src="material?.url"
            controls
            class="video-preview"
          >
            {{ $t('alerts.content-creator.video.unsupported-browser') }}
          </video>
        </div>
        <div
          v-else-if="isPdf"
          class="pdf-preview-container"
        >
          <VuePdfEmbed :source="material.url" />
        </div>
        <div
          v-else
          class="d-flex justify-center align-center pa-10"
        >
          <r-img
            :src="material?.icon"
            height="200px"
          />
        </div>
      </v-card-text>
      <v-card-actions
        :class="{
          'material-preview__actions-padding': isMobile,
        }"
      >
        <v-btn
          color="grey-darken-3"
          @click="close"
          >{{ $t('labels.close') }}</v-btn
        >
        <v-spacer />
        <UpgradeInfoDialog :show-info="showUpselling">
          <template v-slot="{ props: upgradeProps }">
            <v-btn
              v-bind="upgradeProps"
              :href="
                material?.url && !showUpselling
                  ? setQueryParams(material.url, { attachment: 'true' })
                  : ''
              "
            >
              <v-icon icon="mdi-download" />
              {{ $t('ad-materials.preview.download') }}
            </v-btn>
          </template>
        </UpgradeInfoDialog>
        <v-btn
          v-if="canCreateContent"
          @click="goToCreateContent"
        >
          <v-icon icon="mdi-brain" />
          {{ $t('ad-materials.preview.create-content') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script lang="ts" setup>
import { computed, defineAsyncComponent } from 'vue'
import { useRouter } from 'vue-router'
import { useDisplay } from 'vuetify'

import UpgradeInfoDialog from '@/components/UpgradeInfoDialog.vue'
import { setQueryParams } from '@/lib/url'

import { isPdfFile, isVideoFile } from './fileTypes'
import type { FlattenedMaterial } from './index.vue'

const VuePdfEmbed = defineAsyncComponent(() => import('vue-pdf-embed'))

type Props = {
  modelValue: string
  material: FlattenedMaterial | null
  isImage: boolean
  showUpselling?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  showUpselling: false,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
}>()

const router = useRouter()
const { smAndDown: isMobile } = useDisplay()

const internalModelValue = computed({
  get: () => !!props.modelValue,
  set: () => close(),
})

const isVideo = computed(
  () =>
    props.material?.assetType === 'VIDEO' ||
    (!!props.material?.url && isVideoFile(props.material.url))
)

const isPdf = computed(() => !!props.material?.url && isPdfFile(props.material.url))

const canCreateContent = computed(
  () =>
    !!props.material &&
    props.material?.topicId &&
    props.material?.imageSource &&
    props.material.assetType === 'SOCIAL_IMAGE'
)
const close = () => emit('update:modelValue', '')

const goToCreateContent = () => {
  if (!props.material) return
  const { topicId, id: imageId, imageSource } = props.material
  router.push({
    name: 'content-creator',
    query: { topicId, imageId, imageSource, section: 'creator' },
  })
  emit('update:modelValue', '')
}
</script>
<style scoped>
.material-preview__header {
  background-color: rgb(var(--v-theme-primary));
  color: white;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.material-preview__actions-padding {
  padding-bottom: 5rem;
}

.video-preview-container {
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #000;
  border-radius: 4px;
}

.video-preview {
  width: 100%;
  max-height: 500px;
  border-radius: 4px;
}

.pdf-preview-container {
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}
</style>
